import { Icon, type IconName } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/ui.store';
import { A, useLocation } from '@solidjs/router';
import { hasAnyReleaseCap } from '@uchat-team-management/shared';
import { For, type JSX, Show, createSignal } from 'solid-js';
import { NavItem } from './NavItem';
import { UserPanel } from './UserPanel';

interface NavLink {
  href: string;
  label: string;
  icon: IconName;
  requirePermission?: string;
  /** Custom visibility check (UCH-419 scoped release grants). */
  show?: (perms: ReadonlySet<string>) => boolean;
  /** Exact-path active matching for the child link. */
  end?: boolean;
  children?: NavLink[];
}

const NAV_LINKS: NavLink[] = [
  { href: '/welcome', label: 'Home', icon: 'home', end: true },
  {
    href: '/devices',
    label: 'Devices',
    icon: 'smartphone',
    children: [
      { href: '/devices', label: 'All', icon: 'smartphone', requirePermission: 'devices:list' },
      { href: '/devices/me', label: 'My Devices', icon: 'smartphone', end: true },
      {
        href: '/devices/register',
        label: 'Register Device',
        icon: 'document',
        end: true,
        requirePermission: 'devices:register',
      },
    ],
  },
  { href: '/polls', label: 'Polls', icon: 'polling', requirePermission: 'polls:list' },
  {
    href: '/release',
    label: 'Release',
    icon: 'release',
    children: [
      {
        href: '/release/versions',
        label: 'Versions',
        icon: 'version-control',
        show: (perms) => hasAnyReleaseCap(perms, 'view'),
      },
      {
        href: '/release/compare',
        label: 'Compare',
        icon: 'compare',
        show: (perms) => hasAnyReleaseCap(perms, 'view'),
      },
      {
        href: '/release/desktop-updates',
        label: 'Desktop Updates',
        icon: 'computer',
        end: true,
        show: (perms) => hasAnyReleaseCap(perms, 'view', 'messenger-desktop'),
      },
      {
        href: '/release/products',
        label: 'Products',
        icon: 'product',
        // UCH-431: product catalogue/config is a management surface, not a
        // per-release view — gate the menu on the manage key.
        requirePermission: 'releases:products:manage',
      },
      {
        href: '/release/apple-udids',
        label: 'Apple UDID',
        icon: 'apple',
        requirePermission: 'releases:udids:manage',
      },
    ],
  },
  {
    href: '/runners',
    label: 'CI Runners',
    icon: 'server',
    children: [
      {
        href: '/runners',
        label: 'Overview',
        icon: 'home',
        end: true,
        requirePermission: 'runners:view',
      },
      {
        href: '/runners/machines',
        label: 'Machines',
        icon: 'computer',
        requirePermission: 'runners:view',
      },
      {
        href: '/runners/runners',
        label: 'Runners',
        icon: 'server',
        requirePermission: 'runners:view',
      },
      {
        href: '/runners/queue',
        label: 'Job Queue',
        icon: 'checklist-plan',
        requirePermission: 'runners:view',
      },
      {
        href: '/runners/history',
        label: 'History',
        icon: 'calendar',
        requirePermission: 'runners:view',
      },
    ],
  },
  { href: '/admin/users', label: 'Users', icon: 'users', requirePermission: 'users:list' },
  { href: '/admin/roles', label: 'Roles', icon: 'lock', requirePermission: 'roles:list' },
];

interface SidebarProps {
  variant: 'desktop' | 'drawer';
  onLinkClick?: () => void;
}

export const Sidebar = (props: SidebarProps): JSX.Element => {
  const { session } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const location = useLocation();

  const isCollapsed = () => props.variant === 'desktop' && sidebarCollapsed();

  const canSee = (link: Pick<NavLink, 'requirePermission' | 'show'>): boolean => {
    const s = session();
    if (link.show) return s ? link.show(s.permissions) : false;
    if (!link.requirePermission) return true;
    return s?.permissions.has(link.requirePermission) ?? false;
  };

  // "All" is also active on /devices/:id detail pages — but never on the
  // sibling static routes, so only one menu item lights up at a time.
  const allDevicesActive = () => {
    const p = location.pathname;
    return (
      p === '/devices' ||
      (p.startsWith('/devices/') &&
        !p.startsWith('/devices/me') &&
        !p.startsWith('/devices/register'))
    );
  };

  const groupActive = (link: NavLink) => location.pathname.startsWith(link.href);

  // Collapsible submenu groups — open by default.
  const [closedGroups, setClosedGroups] = createSignal<Record<string, boolean>>({});
  const isGroupOpen = (href: string) => !closedGroups()[href];
  const toggleGroup = (href: string) =>
    setClosedGroups((prev) => ({ ...prev, [href]: !prev[href] }));

  return (
    <aside
      class={cn(
        'flex h-full flex-col border-r border-neutral-200 bg-white text-neutral-900',
        'dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100',
        'transition-[width] duration-200 motion-reduce:transition-none',
        props.variant === 'desktop' && (isCollapsed() ? 'w-14' : 'w-60'),
        props.variant === 'desktop' && 'min-h-[30rem]',
        props.variant === 'drawer' && 'w-72',
      )}
    >
      <div class="flex shrink-0 items-center justify-between px-3 py-4">
        <Show when={!isCollapsed()}>
          <A
            href="/welcome"
            onClick={props.onLinkClick}
            class="font-semibold tracking-tight text-brand-600 dark:text-brand-400"
          >
            UChat - Hera
          </A>
        </Show>
        <Show when={props.variant === 'desktop'}>
          <button
            type="button"
            onClick={toggleSidebar}
            class="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label={sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon
              name="angle-small-left"
              class={cn(
                'transition-transform duration-200 motion-reduce:transition-none',
                sidebarCollapsed() && 'rotate-180',
              )}
            />
          </button>
        </Show>
      </div>

      {/* *:shrink-0 keeps item heights fixed so overflow scrolls instead of squashing.
          Desktop: the nav only scrolls when the viewport is at least the sidebar's
          min height (30rem) — below that the whole sidebar scrolls via AppLayout's
          wrapper, so the nav must grow naturally instead of nesting a scrollbar. */}
      <nav
        class={cn(
          'flex flex-1 flex-col gap-1 px-2 *:shrink-0',
          props.variant === 'desktop'
            ? '[@media(min-height:30rem)]:min-h-0 [@media(min-height:30rem)]:overflow-y-auto'
            : 'min-h-0 overflow-y-auto',
        )}
      >
        <For each={NAV_LINKS}>
          {(link) =>
            link.children ? (
              <Show when={link.children.some((c) => canSee(c))}>
                <Show
                  when={!isCollapsed()}
                  fallback={
                    // Collapsed rail: only the parent icon; clicking expands the
                    // sidebar with the group open so the children are revealed.
                    <button
                      type="button"
                      title={link.label}
                      onClick={() => {
                        setClosedGroups((prev) => ({ ...prev, [link.href]: false }));
                        toggleSidebar();
                      }}
                      class={cn(
                        'flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        groupActive(link)
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                          : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100',
                      )}
                    >
                      <Icon name={link.icon} class="shrink-0" />
                    </button>
                  }
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(link.href)}
                    aria-expanded={isGroupOpen(link.href)}
                    class={cn(
                      'flex w-full items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100',
                      groupActive(link)
                        ? 'text-blue-700 dark:text-blue-200'
                        : 'text-neutral-600 dark:text-neutral-300',
                    )}
                  >
                    <Icon name={link.icon} class="shrink-0" />
                    <span class="whitespace-nowrap">{link.label}</span>
                    <Icon
                      name="angle-small-down"
                      class={cn(
                        'ml-auto shrink-0 transition-transform duration-200 motion-reduce:transition-none',
                        !isGroupOpen(link.href) && '-rotate-90',
                      )}
                    />
                  </button>
                  {/* Expand/collapse via the CSS grid-rows trick so height animates without JS measuring. */}
                  <div
                    inert={!isGroupOpen(link.href)}
                    class={cn(
                      'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
                      isGroupOpen(link.href)
                        ? 'grid-rows-[1fr] opacity-100'
                        : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div class="overflow-hidden">
                      {/* Tree guide line: children visibly belong to the group header. */}
                      <div class="ml-5 flex flex-col gap-1 border-l border-neutral-200 pl-2 dark:border-neutral-800">
                        <For each={link.children}>
                          {(child) => (
                            <Show when={canSee(child)}>
                              <NavItem
                                href={child.href}
                                icon={child.icon}
                                label={child.label}
                                collapsed={false}
                                onClick={props.onLinkClick}
                                end={child.end}
                                active={child.href === '/devices' ? allDevicesActive() : undefined}
                              />
                            </Show>
                          )}
                        </For>
                      </div>
                    </div>
                  </div>
                </Show>
              </Show>
            ) : (
              <Show when={canSee(link)}>
                <NavItem
                  href={link.href}
                  icon={link.icon}
                  label={link.label}
                  collapsed={isCollapsed()}
                  onClick={props.onLinkClick}
                  end={link.end}
                />
              </Show>
            )
          }
        </For>
      </nav>

      <div class="shrink-0 border-t border-neutral-200 dark:border-neutral-800">
        <UserPanel collapsed={isCollapsed()} />
      </div>
    </aside>
  );
};
